// OnboardingView.jsx — ARIA ASKS FOR WHAT SHE NEEDS (Braeden 8/13)
//
//   "we need to build an onboarding process for EVERYTHING on here ... she
//    needs to know what to do in order for her to do her job, same thing with
//    google, leads, jobs, book, systems"
//
// ONE question on screen. Ever. The doctrine is locked: "always one simple
// fucking question." Under it, the thing that answer switches on — because an
// owner who answers a pile of questions and sees nothing change stops
// answering. Everything already answered shows as a capability she now has.
//
// The questions come from GET /api/onboarding/:surface, which owns the
// branching (existing site vs. new one, card payments vs. cash, and so on).
// This file renders whatever it is handed and posts the answer back.
import React from 'react';
import { getApiBase } from './apiBase';

const STYLE = `
.onb { position:fixed; inset:0; z-index:1500; display:flex; align-items:flex-start; justify-content:center;
  padding:4vh 16px; overflow:auto; background:rgba(8,10,16,.62);
  backdrop-filter:blur(5px); -webkit-backdrop-filter:blur(5px); }
.onb__panel { width:min(680px,100%); border-radius:22px; padding:26px clamp(18px,3vw,32px) 30px;
  background:#12161f; box-shadow:0 24px 70px rgba(0,0,0,.45); color:#eaf0f8;
  --ob-ink:#eaf0f8; --ob-mut:rgba(255,255,255,0.56); --ob-dim:rgba(255,255,255,0.38);
  --ob-card:rgba(255,255,255,0.05); --ob-line:rgba(255,255,255,0.12); --ob-acc:#0a84ff; --ob-on:#30d158; }
.onb--light .onb__panel { background:#eef2f8; color:#0e1420;
  --ob-ink:#0e1420; --ob-mut:rgba(0,0,0,0.58); --ob-dim:rgba(0,0,0,0.40);
  --ob-card:#fff; --ob-line:rgba(0,0,0,0.12); --ob-acc:#0a6bd4; --ob-on:#1a7f37; }
.onb__head { display:flex; align-items:flex-start; justify-content:space-between; gap:14px; margin-bottom:6px; }
.onb__eyebrow { font-size:11px; font-weight:800; letter-spacing:.14em; text-transform:uppercase; color:var(--ob-acc); }
.onb__title { font-size:clamp(22px,2.6vw,30px); font-weight:800; letter-spacing:-.03em; line-height:1.1; color:var(--ob-ink); }
.onb__lead { font-size:14px; color:var(--ob-mut); margin-top:4px; }
.onb__close { background:var(--ob-card); border:1px solid var(--ob-line); width:36px; height:36px; border-radius:50%;
  font-size:20px; line-height:1; color:var(--ob-ink); cursor:pointer; flex:0 0 auto; }

.onb__bar { height:5px; border-radius:99px; background:var(--ob-card); margin:16px 0 4px; overflow:hidden; }
.onb__bar i { display:block; height:100%; background:var(--ob-acc); border-radius:99px; transition:width .3s ease; }
.onb__count { font-size:11px; font-weight:700; color:var(--ob-dim); }

.onb__q { font-size:clamp(19px,2.1vw,25px); font-weight:800; letter-spacing:-.02em; line-height:1.25;
  color:var(--ob-ink); margin:22px 0 6px; }
.onb__why { font-size:13px; color:var(--ob-mut); line-height:1.5; margin-bottom:16px; }
.onb__why b { color:var(--ob-acc); font-weight:700; }
.onb__hint { font-size:12px; color:var(--ob-dim); margin:-8px 0 14px; }

.onb__opts { display:flex; flex-direction:column; gap:8px; }
.onb__opt { text-align:left; background:var(--ob-card); border:1px solid var(--ob-line); color:var(--ob-ink);
  border-radius:14px; padding:15px 18px; font-size:15px; font-weight:700; letter-spacing:-.01em;
  cursor:pointer; font-family:inherit; }
.onb__opt:hover { border-color:var(--ob-acc); }
.onb__opt--on { background:var(--ob-acc); border-color:var(--ob-acc); color:#fff; }
.onb__input { width:100%; box-sizing:border-box; background:var(--ob-card); border:1px solid var(--ob-line);
  border-radius:14px; padding:15px 18px; font-size:16px; font-weight:600; color:var(--ob-ink);
  font-family:inherit; outline:none; }
.onb__input:focus { border-color:var(--ob-acc); }
.onb__prices { display:flex; flex-direction:column; gap:8px; }
.onb__price { display:grid; grid-template-columns:1fr 130px; gap:10px; align-items:center; }
.onb__price span { font-size:15px; font-weight:700; color:var(--ob-ink); }

.onb__actions { display:flex; align-items:center; gap:10px; margin-top:16px; flex-wrap:wrap; }
.onb__go { background:var(--ob-acc); border:0; color:#fff; border-radius:999px; padding:13px 26px;
  font-size:15px; font-weight:800; cursor:pointer; font-family:inherit; }
.onb__go[disabled] { opacity:.4; cursor:not-allowed; }
.onb__skip { background:none; border:0; color:var(--ob-dim); font-size:13px; font-weight:700;
  cursor:pointer; font-family:inherit; text-decoration:underline; }

.onb__done { margin-top:22px; padding:18px; border-radius:16px; background:var(--ob-card); border:1px solid var(--ob-line); }
.onb__done b { display:block; font-size:17px; font-weight:800; color:var(--ob-ink); margin-bottom:8px; }
.onb__unlocked { margin-top:20px; padding-top:16px; border-top:1px solid var(--ob-line); }
.onb__ulab { font-size:11px; font-weight:800; letter-spacing:.12em; text-transform:uppercase; color:var(--ob-dim); margin-bottom:8px; }
.onb__u { display:flex; gap:9px; align-items:flex-start; font-size:13px; color:var(--ob-mut); line-height:1.45; margin-bottom:6px; }
.onb__u i { color:var(--ob-on); font-style:normal; font-weight:800; flex:0 0 auto; }
@media (max-width:600px) { .onb__price { grid-template-columns:1fr; } }
`;

/** Multi-select list with a freeform "add your own" row. */
function ListPicker({ step, value, onChange }) {
  const picked = Array.isArray(value) ? value : [];
  const [extra, setExtra] = React.useState('');
  const toggle = (o) => onChange(picked.includes(o) ? picked.filter((x) => x !== o) : [...picked, o]);
  const add = () => { const v = extra.trim(); if (v && !picked.includes(v)) { onChange([...picked, v]); setExtra(''); } };
  return (
    <div className="onb__opts">
      {(step.options || []).map((o) => (
        <button key={o} type="button" className={'onb__opt' + (picked.includes(o) ? ' onb__opt--on' : '')}
                onClick={() => toggle(o)}>{o}</button>
      ))}
      {picked.filter((p) => !(step.options || []).includes(p)).map((p) => (
        <button key={p} type="button" className="onb__opt onb__opt--on" onClick={() => toggle(p)}>{p}</button>
      ))}
      <input className="onb__input" value={extra} onChange={(e) => setExtra(e.target.value)}
             onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
             placeholder={step.freeform ? 'Type one and press Enter' : 'Something else? Type it and press Enter'} />
    </div>
  );
}

export default function OnboardingView({ surfaceKey, theme, ownerId, onClose, onReady }) {
  const [s, setS] = React.useState(null);
  const [draft, setDraft] = React.useState(undefined);
  const [busy, setBusy] = React.useState(false);
  // bumped when an OAuth tab is opened, and on window focus — the connection's
  // truth lives on the server, so coming back re-reads it instead of assuming
  const [recheck, setRecheck] = React.useState(0);
  React.useEffect(() => {
    const onFocus = () => setRecheck((n) => n + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  const base = getApiBase();
  const oid = ownerId || (() => { try { return localStorage.getItem('howto_owner_id') || 'owner-default'; } catch (_) { return 'owner-default'; } })();

  const auth = React.useCallback(() => {
    let tok = ''; try { tok = localStorage.getItem('aria_session_token') || ''; } catch (_) {}
    return tok ? { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  }, []);

  React.useEffect(() => {
    let dead = false;
    fetch(`${base}/api/onboarding/${surfaceKey}?ownerId=${encodeURIComponent(oid)}`, { headers: auth() })
      .then((r) => r.json())
      .then((d) => { if (!dead && d && d.ok) { setS(d.surface); setDraft(undefined); } })
      .catch(() => {});
    return () => { dead = true; };
  }, [base, surfaceKey, oid, auth, recheck]);

  const post = async (path, body) => {
    setBusy(true);
    try {
      const d = await fetch(`${base}/api/onboarding/${surfaceKey}/${path}?ownerId=${encodeURIComponent(oid)}`,
        { method: 'POST', headers: auth(), body: JSON.stringify(body) }).then((r) => r.json());
      if (d && d.ok) {
        setS(d.surface); setDraft(undefined);
        if (d.surface.ready && onReady) onReady(surfaceKey);
      }
    } catch (_) {}
    setBusy(false);
  };

  const step = s && s.next;
  // a choice answers on click — one tap, no confirm button to hunt for
  const send = (v) => post('answer', { stepId: step.id, value: v });

  const canSend = draft !== undefined && draft !== '' && !(Array.isArray(draft) && draft.length === 0);
  const pct = s && s.total ? Math.round((s.answered / s.total) * 100) : 0;

  return (
    <div className={'onb' + (theme === 'day' ? ' onb--light' : '')}
         onClick={(e) => { if (e.currentTarget === e.target) onClose(); }}>
      <style>{STYLE}</style>
      <div className="onb__panel" onClick={(e) => e.stopPropagation()}>
        <div className="onb__head">
          <div>
            <div className="onb__eyebrow">Setting up</div>
            <div className="onb__title">{s ? s.title : '…'}</div>
            {s && <div className="onb__lead">{s.lead}</div>}
          </div>
          <button type="button" className="onb__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {s && (
          <>
            <div className="onb__bar"><i style={{ width: pct + '%' }} /></div>
            <div className="onb__count">{s.answered} of {s.total} answered{s.remaining ? ` · ${s.remaining} left` : ''}</div>
          </>
        )}

        {!s ? null : !step ? (
          <div className="onb__done">
            <b>That's everything I needed for {s.title}.</b>
            <div style={{ fontSize: 14, opacity: .85, lineHeight: 1.5 }}>
              I can do the {s.title.toLowerCase()} work now. Anything you change later, just tell me.
            </div>
            <div className="onb__actions">
              <button type="button" className="onb__go" onClick={onClose}>Open {s.title}</button>
            </div>
          </div>
        ) : (
          <>
            <div className="onb__q">{step.ask}</div>
            {step.unlocks && <div className="onb__why"><b>So I can:</b> {step.unlocks}</div>}
            {step.sheFindsIt && <div className="onb__hint">I'll try to find this myself — correct me if I get it wrong.</div>}

            {step.kind === 'choice' || step.kind === 'confirm' ? (
              <div className="onb__opts">
                {(step.options || ['Yes', 'No']).map((o) => (
                  <button key={o} type="button" className="onb__opt" disabled={busy} onClick={() => send(o)}>{o}</button>
                ))}
              </div>
            ) : step.kind === 'list' ? (
              <>
                <ListPicker step={step} value={draft} onChange={setDraft} />
                <div className="onb__actions">
                  <button type="button" className="onb__go" disabled={!canSend || busy} onClick={() => send(draft)}>Save</button>
                  {step.optional && <button type="button" className="onb__skip" onClick={() => post('skip', { stepId: step.id })}>Skip for now</button>}
                </div>
              </>
            ) : step.kind === 'connect' ? (
              // A real OAuth handoff. The step is NOT marked done by this click —
              // the server decides that from whether tokens actually landed, so
              // the flow re-checks when the tab comes back rather than trusting
              // the button. Anything not built never reaches here at all.
              <div className="onb__actions">
                <button type="button" className="onb__go" disabled={busy}
                        onClick={() => {
                          const u = step.connection && step.connection.connectUrl;
                          if (u) window.open(base + u, '_blank', 'noopener,noreferrer');
                          setRecheck((n) => n + 1);
                        }}>
                  Connect {(step.connection && step.connection.label) || step.provider}
                </button>
                <button type="button" className="onb__skip" onClick={() => post('skip', { stepId: step.id })}>Not now</button>
              </div>
            ) : step.kind === 'money_per_service' ? (
              (() => {
                const svc = (s.steps.find((x) => x.id === 'services') || {}).value;
                const list = Array.isArray(svc) ? svc : [];
                const d = draft && typeof draft === 'object' ? draft : {};
                if (!list.length) {
                  return (
                    <>
                      <input className="onb__input" autoFocus value={typeof draft === 'string' ? draft : ''}
                             onChange={(e) => setDraft(e.target.value)} placeholder="e.g. $250 minimum, $75 a yard" />
                      <div className="onb__actions">
                        <button type="button" className="onb__go" disabled={!canSend || busy} onClick={() => send(draft)}>Save</button>
                      </div>
                    </>
                  );
                }
                return (
                  <>
                    <div className="onb__prices">
                      {list.map((name) => (
                        <label className="onb__price" key={name}>
                          <span>{name}</span>
                          <input className="onb__input" inputMode="decimal" value={d[name] || ''}
                                 onChange={(e) => setDraft({ ...d, [name]: e.target.value })} placeholder="$" />
                        </label>
                      ))}
                    </div>
                    <div className="onb__actions">
                      <button type="button" className="onb__go" disabled={busy || !Object.keys(d).length} onClick={() => send(d)}>Save prices</button>
                      <button type="button" className="onb__skip" onClick={() => post('skip', { stepId: step.id })}>They all depend — skip</button>
                    </div>
                  </>
                );
              })()
            ) : (
              <>
                <input className="onb__input" autoFocus
                       type={step.kind === 'phone' ? 'tel' : 'text'}
                       value={typeof draft === 'string' ? draft : ''}
                       onChange={(e) => setDraft(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter' && canSend) send(draft); }}
                       placeholder={step.kind === 'url' ? 'yoursite.com' : step.kind === 'phone' ? '(316) 555-0100' : 'Type your answer'} />
                <div className="onb__actions">
                  <button type="button" className="onb__go" disabled={!canSend || busy} onClick={() => send(draft)}>Save</button>
                  {step.optional && <button type="button" className="onb__skip" onClick={() => post('skip', { stepId: step.id })}>Skip for now</button>}
                </div>
              </>
            )}
          </>
        )}

        {s && s.unlocked && s.unlocked.length > 0 && (
          <div className="onb__unlocked">
            <div className="onb__ulab">What you've switched on</div>
            {s.unlocked.map((u, i) => <div className="onb__u" key={i}><i>✓</i><span>{u}</span></div>)}
          </div>
        )}

        {/* OUR gaps, not his. Never asked as a question he could answer, never
            hidden either — this is what she still cannot do, and why. */}
        {s && s.platformGaps && s.platformGaps.length > 0 && (
          <div className="onb__unlocked">
            <div className="onb__ulab">Not on you — still being built</div>
            {s.platformGaps.map((g, i) => (
              <div className="onb__u" key={i}>
                <i style={{ color: 'var(--ob-mut)' }}>·</i>
                <span><b style={{ color: 'var(--ob-ink)' }}>{g.label}</b> — {g.reason}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
